import { Box, Typography } from '@mui/material';
import { useWatch } from 'react-hook-form';
import { useGetMany, useTranslate } from 'react-admin';
import { useMemo } from 'react';
import { summaryStepLayoutStyles } from '@/themes/commonStyles';
import { criteriaConfig } from '@/constants/CampaignCriteria';
import { extractCriteriaIds } from '@/helpers/marketing/extractCriteriaIds';
import { formatCriteriaValue } from '@/helpers/marketing/formatCriteriaValue';
import CriteriaValueChip from './CriteriaValueChip';

const getGroupKey = group => group.id ?? `${group.baseOption}-${JSON.stringify(group.baseValue ?? '')}`;
const getRowKey = row => row.id ?? `${row.operator}-${row.option}-${JSON.stringify(row.value ?? '')}`;
const getSubgroupKey = subgroup => subgroup.id ?? subgroup.rows?.map(getRowKey).join('|');

const SummaryStepCriteriaInfo = () => {
    const translate = useTranslate();
    const criteriaRaw = useWatch({ name: 'criteria' });
    const criteria = useMemo(() => criteriaRaw || [], [criteriaRaw]);

    const { categoryIds, productIds } = useMemo(() => extractCriteriaIds(criteria), [criteria]);

    const { data: categories = [], error: categoriesError } = useGetMany('product-categories-mock', {
        ids: categoryIds,
    });
    const { data: products = [], error: productsError } = useGetMany('product-mock', {
        ids: productIds,
    });

    if (categoriesError || productsError) {
        console.error('Błąd podczas ładowania danych kryteriów:', categoriesError || productsError);
    }

    if (!Array.isArray(criteria) || criteria.length === 0) {
        return null;
    }

    const renderCriteriaGroup = group => {
        const config = criteriaConfig[group.baseOption];
        const baseLabel = config?.label || group.baseOption;
        const baseValue = formatCriteriaValue(group.baseOption, group.baseValue, products, translate);

        return (
            <Box key={getGroupKey(group)} sx={summaryStepLayoutStyles.criteriaGroup}>
                <Box sx={summaryStepLayoutStyles.criteriaSubgroup}>
                    <CriteriaValueChip formattedValue={baseValue} label={baseLabel} categories={categories} isMainChip={true} />
                </Box>
                {group.subgroups?.map(subgroup => (
                    <Box key={getSubgroupKey(subgroup)} sx={summaryStepLayoutStyles.criteriaSubgroup}>
                        {subgroup.rows?.length > 0 && (
                            <Box sx={summaryStepLayoutStyles.criteriaRows}>
                                {subgroup.rows.map(row => {
                                    const rowConfig = criteriaConfig[row.option];
                                    const rowLabel = rowConfig?.label || row.option;
                                    const rowValue = formatCriteriaValue(row.option, row.value, products);

                                    return (
                                        <Box key={getRowKey(row)} sx={summaryStepLayoutStyles.flexRowBaseline}>
                                            <Typography component='span' variant='inherit'>
                                                {row.operator}
                                            </Typography>
                                            <CriteriaValueChip formattedValue={rowValue} label={rowLabel} categories={categories} />
                                        </Box>
                                    );
                                })}
                            </Box>
                        )}
                    </Box>
                ))}
            </Box>
        );
    };

    return <Box sx={summaryStepLayoutStyles.flexColumn}>{criteria.map(group => renderCriteriaGroup(group))}</Box>;
};

export default SummaryStepCriteriaInfo;
